import React, { useState, useCallback, useMemo } from 'react';
import useDebtData from '../../hooks/useDebtData';
import { useCurrency } from '../../contexts/CurrencyContext';
import { formatSmartCurrency } from '../../utils/formatCurrency';
import { useTheme } from '../../contexts/ThemeContext';

const LIABILITY_CATEGORIES = [
  { value: 'credit_card', label: 'Credit Card', emoji: '💳' },
  { value: 'auto_loan', label: 'Auto Loan', emoji: '🚗' },
  { value: 'student_loan', label: 'Student Loan', emoji: '🎓' },
  { value: 'mortgage', label: 'Mortgage', emoji: '🏠' },
  { value: 'personal_loan', label: 'Personal Loan', emoji: '🤝' },
  { value: 'other', label: 'Other', emoji: '📋' },
];

const DebtPage = () => {
  const {
    liabilities, ious, loading,
    addLiability, deleteLiability,
    addIOU, settleIOU, unsettleIOU, deleteIOU,
    activeIOUs, settledIOUs, owedToMe, iOwe,
    totalDebt, totalMinPayment, netIOUPosition,
  } = useDebtData();
  const { currencyCode } = useCurrency();
  const { theme } = useTheme();
  const [tab, setTab] = useState('credit');
  const [showAddDebt, setShowAddDebt] = useState(false);
  const [showAddIOU, setShowAddIOU] = useState(false);
  const [debtForm, setDebtForm] = useState({ title: '', currentBalance: '', interestRate: '', monthlyPayment: '', liabilityCategory: 'credit_card', emoji: '💳', creditLimit: '' });
  const [iouForm, setIOUForm] = useState({ title: '', personName: '', amount: '', direction: 'i_owe' });

  const fmt = useCallback((cents, mode = 'long') => formatSmartCurrency(cents / 100, { mode, currencyCode }), [currencyCode]);

  const creditCards = useMemo(() => liabilities.filter((l) => l.liabilityCategory === 'credit_card'), [liabilities]);
  const debts = useMemo(() => liabilities.filter((l) => l.liabilityCategory !== 'credit_card'), [liabilities]);
  const paidCount = useMemo(() => liabilities.filter((l) => (l.currentBalance || 0) === 0).length, [liabilities]);

  // Utilization
  const totalLimit = useMemo(() => liabilities.reduce((s, l) => s + (l.creditLimit || 0), 0), [liabilities]);
  const utilizationPct = totalLimit > 0 ? Math.round((totalDebt / totalLimit) * 100) : 0;

  const handleAddDebt = async () => {
    if (!debtForm.title.trim() || !debtForm.currentBalance) return;
    await addLiability({ title: debtForm.title.trim(), currentBalance: Math.round(parseFloat(debtForm.currentBalance) * 100), interestRate: parseFloat(debtForm.interestRate) || 0, monthlyPayment: Math.round((parseFloat(debtForm.monthlyPayment) || 0) * 100), liabilityCategory: debtForm.liabilityCategory, emoji: debtForm.emoji, creditLimit: debtForm.creditLimit ? Math.round(parseFloat(debtForm.creditLimit) * 100) : 0 });
    setDebtForm({ title: '', currentBalance: '', interestRate: '', monthlyPayment: '', liabilityCategory: 'credit_card', emoji: '💳', creditLimit: '' });
    setShowAddDebt(false);
  };

  const handleAddIOU = async () => {
    if (!iouForm.personName.trim() || !iouForm.amount) return;
    await addIOU({ title: iouForm.title.trim() || `IOU - ${iouForm.personName.trim()}`, personName: iouForm.personName.trim(), amount: Math.round(parseFloat(iouForm.amount) * 100), direction: iouForm.direction });
    setIOUForm({ title: '', personName: '', amount: '', direction: 'i_owe' });
    setShowAddIOU(false);
  };

  if (loading) return <div style={{ padding: 24, color: theme.tertiaryText }}>Loading...</div>;

  return (
    <div style={{ backgroundColor: theme.backgroundColor, minHeight: '100%', padding: '0 0 100px' }}>

      {/* Debt Overview Hero */}
      <div style={{ padding: '24px 20px 20px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: theme.tertiaryText, textTransform: 'uppercase', letterSpacing: 1.2 }}>Debt Overview</div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 4 }}>
          <div>
            <div style={{ fontSize: 33, fontWeight: 800, color: theme.textColor, letterSpacing: -0.5 }}>{fmt(totalDebt, 'short')}</div>
            <div style={{ fontSize: 11, color: theme.tertiaryText, marginTop: 2 }}>
              {fmt(totalDebt - (activeIOUs.reduce((s, i) => s + (i.direction === 'i_owe' ? i.amount : 0), 0)), 'short')} debts
              {activeIOUs.length > 0 && <> · {fmt(activeIOUs.reduce((s, i) => s + (i.direction === 'i_owe' ? i.amount : 0), 0), 'short')} IOUs</>}
            </div>
          </div>
          {/* Utilization circle */}
          {totalLimit > 0 && (
            <div style={{
              width: 60, height: 60, borderRadius: 30,
              background: `conic-gradient(rgba(96,165,250,0.5) ${utilizationPct * 3.6}deg, #F3F4F6 0deg)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: theme.backgroundColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: theme.textColor }}>{utilizationPct}%</span>
                <span style={{ fontSize: 8, color: theme.tertiaryText }}>utilization</span>
              </div>
            </div>
          )}
        </div>

        {/* Stat boxes */}
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <StatBox theme={theme} label="Cards" value={creditCards.length} />
          <StatBox theme={theme} label="Min Due" value={fmt(totalMinPayment, 'short')} />
          <StatBox theme={theme} label={`${paidCount}/${liabilities.length} Paid`} value="✓" color="#10B981" bg="#ECFDF5" />
        </div>
      </div>

      {/* Tab pills */}
      <div style={{ display: 'flex', gap: 0, padding: '0 20px', marginBottom: 16 }}>
        {[
          { key: 'credit', label: 'Credit Cards' },
          { key: 'debts', label: 'Debts', badge: debts.length || null },
          { key: 'ious', label: 'IOUs', badge: netIOUPosition !== 0 ? (netIOUPosition > 0 ? `+${fmt(netIOUPosition, 'short')}` : fmt(netIOUPosition, 'short')) : null },
        ].map((t) => {
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '8px 14px', borderRadius: 10, border: active ? '1.5px solid #222' : '1px solid #E5E7EB',
              backgroundColor: active ? '#fff' : 'transparent', cursor: 'pointer',
              fontSize: 14, fontWeight: active ? 600 : 400, color: active ? '#222' : '#999',
              marginRight: 6, display: 'flex', alignItems: 'center', gap: 6,
            }}>
              {t.label}
              {t.badge && (
                <span style={{ fontSize: 12, fontWeight: 700, color: t.key === 'ious' ? '#3FD082' : '#FF9500', backgroundColor: t.key === 'ious' ? '#ECFDF5' : '#FFF7ED', padding: '1px 6px', borderRadius: 6 }}>{t.badge}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Credit Cards tab */}
      {tab === 'credit' && (
        <div style={{ padding: '0 16px' }}>
          {/* Payoff strategy */}
          {creditCards.length > 1 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: theme.textColor }}>Payoff strategy</span>
                <span style={{ fontSize: 13, fontWeight: 500, color: '#60A5FA', backgroundColor: '#E8F0FE', padding: '3px 10px', borderRadius: 6 }}>Avalanche</span>
                <span style={{ fontSize: 13, fontWeight: 500, color: theme.tertiaryText, padding: '3px 10px' }}>Snowball</span>
              </div>
              <div style={{ fontSize: 12, color: theme.tertiaryText }}>Sorted by highest APR — saves the most interest over time.</div>
            </div>
          )}

          {creditCards.map((card, i) => <CreditCardRow theme={theme} key={card.id} card={card} fmt={fmt} isFirst={i === 0 && creditCards.length > 1} onDelete={deleteLiability} />)}
          {creditCards.length === 0 && !showAddDebt && (
            <EmptyMsg text="No credit cards tracked" />
          )}
          <AddForm theme={theme} show={showAddDebt} type="credit_card" debtForm={debtForm} setDebtForm={setDebtForm} onAdd={handleAddDebt} onCancel={() => setShowAddDebt(false)} onShow={() => setShowAddDebt(true)} />
        </div>
      )}

      {/* Debts tab */}
      {tab === 'debts' && (
        <div style={{ padding: '0 16px' }}>
          {debts.map((debt) => <DebtRow theme={theme} key={debt.id} debt={debt} fmt={fmt} onDelete={deleteLiability} />)}
          {debts.length === 0 && !showAddDebt && <EmptyMsg text="No debts tracked" />}
          <AddForm theme={theme} show={showAddDebt} type="other" debtForm={debtForm} setDebtForm={setDebtForm} onAdd={handleAddDebt} onCancel={() => setShowAddDebt(false)} onShow={() => setShowAddDebt(true)} />
        </div>
      )}

      {/* IOUs tab */}
      {tab === 'ious' && (
        <div style={{ padding: '0 16px' }}>
          {/* IOU summary cards */}
          {(owedToMe.length > 0 || iOwe.length > 0) && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <div style={{ flex: 1, border: `1px solid ${theme.borderColor}`, borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: theme.tertiaryText, marginBottom: 4 }}>Owed to Me</div>
                <div style={{ fontSize: 21, fontWeight: 700, color: '#10B981' }}>{fmt(owedToMe.reduce((s, i) => s + (i.amount || 0), 0))}</div>
              </div>
              <div style={{ flex: 1, border: `1px solid ${theme.borderColor}`, borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: theme.tertiaryText, marginBottom: 4 }}>I Owe</div>
                <div style={{ fontSize: 21, fontWeight: 700, color: '#EF4444' }}>{fmt(iOwe.reduce((s, i) => s + (i.amount || 0), 0))}</div>
              </div>
            </div>
          )}

          {activeIOUs.map((iou) => <IOURow theme={theme} key={iou.id} iou={iou} fmt={fmt} onSettle={settleIOU} onDelete={deleteIOU} />)}
          {settledIOUs.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.tertiaryText, textTransform: 'uppercase', letterSpacing: 0.5, padding: '16px 0 4px' }}>Settled</div>
              {settledIOUs.map((iou) => <IOURow theme={theme} key={iou.id} iou={iou} fmt={fmt} onUnsettle={unsettleIOU} onDelete={deleteIOU} settled />)}
            </>
          )}
          {ious.length === 0 && !showAddIOU && <EmptyMsg text="No IOUs tracked" />}

          {showAddIOU ? (
            <div style={{ backgroundColor: theme.surfaceBackground, borderRadius: 16, padding: 20, marginTop: 12, border: `1px solid ${theme.borderColor}` }}>
              <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 16, color: theme.textColor }}>Add IOU</div>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Direction</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[{ value: 'i_owe', label: 'I Owe', emoji: '📤' }, { value: 'owed_to_me', label: 'Owed to Me', emoji: '📥' }].map((d) => (
                    <button key={d.value} onClick={() => setIOUForm({ ...iouForm, direction: d.value })} style={{ flex: 1, padding: 8, borderRadius: 10, fontSize: 14, cursor: 'pointer', border: iouForm.direction === d.value ? '2px solid #3B82F6' : '1px solid #E5E7EB', backgroundColor: iouForm.direction === d.value ? '#E8F0FE' : '#fff', color: iouForm.direction === d.value ? '#3B82F6' : '#999', fontWeight: iouForm.direction === d.value ? 600 : 400 }}>{d.emoji} {d.label}</button>
                  ))}
                </div>
              </div>
              <FormField label="Person" value={iouForm.personName} onChange={(v) => setIOUForm({ ...iouForm, personName: v })} placeholder="e.g., Marcus, Mom" />
              <FormField label="Amount" value={iouForm.amount} onChange={(v) => setIOUForm({ ...iouForm, amount: v })} placeholder="0.00" type="number" />
              <FormField label="Note (optional)" value={iouForm.title} onChange={(v) => setIOUForm({ ...iouForm, title: v })} placeholder="What's it for?" />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleAddIOU} style={primaryBtn}>Add IOU</button>
                <button onClick={() => setShowAddIOU(false)} style={secondaryBtn}>Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAddIOU(true)} style={addBtnStyle}>+ Add IOU</button>
          )}
        </div>
      )}
    </div>
  );
};

// ── Sub-components ──

const StatBox = ({ label, value, color, bg, theme }) => (
  <div style={{ flex: 1, border: `1px solid ${theme.borderColor}`, borderRadius: 10, padding: '8px 6px', textAlign: 'center', backgroundColor: bg || 'transparent' }}>
    <div style={{ fontSize: 14, fontWeight: 700, color: color || '#222' }}>{value}</div>
    <div style={{ fontSize: 11, fontWeight: 600, color: theme.tertiaryText, marginTop: 3 }}>{label}</div>
  </div>
);

const CreditCardRow = ({ card, fmt, isFirst, onDelete, theme }) => {
  const balance = card.currentBalance || 0;
  const limit = card.creditLimit || 0;
  const util = limit > 0 ? Math.round((balance / limit) * 100) : 0;
  const isPaid = balance === 0;
  const last4 = card.title?.match(/\d{4}$/)?.[0];

  return (
    <div style={{
      borderRadius: 18, padding: '12px 14px', marginBottom: 8,
      border: isFirst ? '1.5px solid #3B82F6' : '1px solid #E5E7EB',
      borderLeft: isFirst ? '4px solid #3B82F6' : undefined,
      backgroundColor: isFirst ? 'rgba(59,130,246,0.03)' : '#fff',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Card icon */}
        <div style={{
          width: 44, height: 30, borderRadius: 6,
          background: 'linear-gradient(135deg, #3B82F6, #1D4ED8)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>💳</span>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: theme.textColor }}>
              {card.title}{last4 ? '' : ''}</span>
            {isFirst && (
              <span style={{ fontSize: 11, fontWeight: 700, color: '#3B82F6', backgroundColor: 'rgba(59,130,246,0.12)', padding: '2px 8px', borderRadius: 6 }}>PAY FIRST</span>
            )}
          </div>
          {/* Utilization bar */}
          {limit > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <div style={{ flex: 1, height: 4, backgroundColor: '#E5E7EB', borderRadius: 2, overflow: 'hidden', maxWidth: 120 }}>
                <div style={{ height: '100%', width: `${util}%`, backgroundColor: '#3B82F6', borderRadius: 2 }} />
              </div>
              <span style={{ fontSize: 11, color: theme.tertiaryText }}>{util}% used</span>
            </div>
          )}
        </div>

        {/* Amount */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: theme.textColor }}>{fmt(balance, 'short')}</div>
          {limit > 0 && <div style={{ fontSize: 11, color: theme.disabledText }}>of {fmt(limit, 'short')}</div>}
        </div>

        <button onClick={() => { if (window.confirm(`Delete "${card.title}"?`)) onDelete(card.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E5E7EB', fontSize: 14 }} onMouseEnter={(e) => (e.currentTarget.style.color = '#EF4444')} onMouseLeave={(e) => (e.currentTarget.style.color = '#E5E7EB')}>✕</button>
      </div>

      {/* APR + Min + Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, marginLeft: 54, fontSize: 12, color: theme.tertiaryText }}>
        {card.interestRate > 0 && <span>{card.interestRate}% APR</span>}
        {card.monthlyPayment > 0 && <span>Min {fmt(card.monthlyPayment, 'short')}</span>}
        {card.dueDay && <span>Due {card.dueDay}st</span>}
        <span style={{
          marginLeft: 'auto', fontSize: 11, fontWeight: 600,
          color: isPaid ? '#10B981' : '#FF9500',
          backgroundColor: isPaid ? '#ECFDF5' : '#FFF7ED',
          padding: '2px 8px', borderRadius: 6,
        }}>
          {isPaid ? 'Paid' : 'Unpaid'}
        </span>
      </div>
    </div>
  );
};

const DebtRow = ({ debt, fmt, onDelete, theme }) => {
  const cat = LIABILITY_CATEGORIES.find((c) => c.value === debt.liabilityCategory) || LIABILITY_CATEGORIES[5];
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '12px 4px', borderBottom: `1px solid ${theme.lightBorder}`, gap: 10 }}>
      <span style={{ fontSize: 20 }}>{debt.emoji || cat.emoji}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: theme.textColor }}>{debt.title}</div>
        <div style={{ fontSize: 12, color: theme.tertiaryText }}>
          {debt.interestRate > 0 && `${debt.interestRate}% APR · `}{cat.label}
        </div>
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: theme.textColor }}>{fmt(debt.currentBalance)}</div>
      <button onClick={() => { if (window.confirm(`Delete?`)) onDelete(debt.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E5E7EB', fontSize: 13 }} onMouseEnter={(e) => (e.currentTarget.style.color = '#EF4444')} onMouseLeave={(e) => (e.currentTarget.style.color = '#E5E7EB')}>✕</button>
    </div>
  );
};

const IOURow = ({ iou, fmt, onSettle, onUnsettle, onDelete, settled, theme }) => {
  const isOwedToMe = iou.direction === 'owed_to_me';
  const initials = (iou.personName || '?').split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
  const avatarColor = isOwedToMe ? '#10B981' : '#60A5FA';

  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${theme.lightBorder}`, opacity: settled ? 0.5 : 1, gap: 12 }}>
      <div style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: avatarColor + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <span style={{ fontSize: 18, fontWeight: 700, color: avatarColor }}>{initials}</span>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: theme.textColor }}>{iou.personName}</div>
        <div style={{ fontSize: 12, color: theme.tertiaryText }}>{isOwedToMe ? 'Owes you' : 'You owe'}</div>
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: isOwedToMe ? '#10B981' : '#EF4444' }}>{fmt(iou.amount)}</div>
      {!settled && onSettle && <button onClick={() => onSettle(iou.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3FD082', fontSize: 14 }}>✓</button>}
      {settled && onUnsettle && <button onClick={() => onUnsettle(iou.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#60A5FA', fontSize: 13 }}>↩</button>}
      <button onClick={() => { if (window.confirm('Delete?')) onDelete(iou.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E5E7EB', fontSize: 13 }} onMouseEnter={(e) => (e.currentTarget.style.color = '#EF4444')} onMouseLeave={(e) => (e.currentTarget.style.color = '#E5E7EB')}>✕</button>
    </div>
  );
};

const EmptyMsg = ({ text }) => (
  <div style={{ textAlign: 'center', padding: '40px 20px', fontSize: 14, color: '#999' }}>{text}</div>
);

const AddForm = ({ show, type, debtForm, setDebtForm, onAdd, onCancel, onShow, theme }) => {
  if (show) return (
    <div style={{ backgroundColor: theme.surfaceBackground, borderRadius: 16, padding: 20, marginTop: 12, border: `1px solid ${theme.borderColor}` }}>
      <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 16, color: theme.textColor }}>Add {type === 'credit_card' ? 'Credit Card' : 'Debt'}</div>
      {type !== 'credit_card' && (
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Category</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {LIABILITY_CATEGORIES.filter((c) => c.value !== 'credit_card').map((cat) => (
              <button key={cat.value} onClick={() => setDebtForm({ ...debtForm, liabilityCategory: cat.value, emoji: cat.emoji })} style={{ padding: '5px 12px', borderRadius: 7, fontSize: 13, cursor: 'pointer', border: debtForm.liabilityCategory === cat.value ? '2px solid #3B82F6' : '1px solid #E5E7EB', backgroundColor: debtForm.liabilityCategory === cat.value ? '#E8F0FE' : '#fff', color: debtForm.liabilityCategory === cat.value ? '#3B82F6' : '#999', fontWeight: debtForm.liabilityCategory === cat.value ? 600 : 400 }}>{cat.emoji} {cat.label}</button>
            ))}
          </div>
        </div>
      )}
      <FormField label="Name" value={debtForm.title} onChange={(v) => setDebtForm({ ...debtForm, title: v })} placeholder={type === 'credit_card' ? 'e.g., Chase Sapphire ~1234' : 'e.g., Car Loan'} />
      <FormField label="Balance" value={debtForm.currentBalance} onChange={(v) => setDebtForm({ ...debtForm, currentBalance: v })} placeholder="0.00" type="number" />
      {type === 'credit_card' && <FormField label="Credit Limit" value={debtForm.creditLimit} onChange={(v) => setDebtForm({ ...debtForm, creditLimit: v })} placeholder="0.00" type="number" />}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormField label="APR %" value={debtForm.interestRate} onChange={(v) => setDebtForm({ ...debtForm, interestRate: v })} placeholder="0" type="number" />
        <FormField label="Min. Payment" value={debtForm.monthlyPayment} onChange={(v) => setDebtForm({ ...debtForm, monthlyPayment: v })} placeholder="0.00" type="number" />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onAdd} style={primaryBtn}>Add</button>
        <button onClick={onCancel} style={secondaryBtn}>Cancel</button>
      </div>
    </div>
  );
  return <button onClick={onShow} style={addBtnStyle}>+ Add {type === 'credit_card' ? 'Credit Card' : 'Debt'}</button>;
};

const FormField = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={labelStyle}>{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} step={type === 'number' ? '0.01' : undefined} style={inputStyle} />
  </div>
);

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#999', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 };
const inputStyle = { width: '100%', padding: '12px 14px', border: '1px solid #999', borderRadius: 10, fontSize: 16, outline: 'none', boxSizing: 'border-box', color: 'inherit', backgroundColor: 'transparent' };
const primaryBtn = { flex: 1, padding: 12, backgroundColor: '#222', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer' };
const secondaryBtn = { padding: '12px 20px', backgroundColor: 'transparent', color: '#999', border: '1px solid #999', borderRadius: 12, fontSize: 16, cursor: 'pointer' };
const addBtnStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: 14, border: '2px dashed #999', borderRadius: 16, backgroundColor: 'transparent', cursor: 'pointer', color: '#999', fontSize: 15, fontWeight: 500, marginTop: 12 };

export default DebtPage;
